import { useContext, createContext, useEffect, useState } from "react";
import {auth, db, functions} from '../firebase'
import { httpsCallable } from "firebase/functions";
import { 
    GoogleAuthProvider,
    signInWithPopup,
    // signInWithRedirect,
    signOut,
    deleteUser,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification,
    updateProfile
 } from "firebase/auth";
import { doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { openInNewTab } from '../utils/popup'

const AuthContext = createContext()

const FREE_LIMIT = 20

// Days in a given UTC month (month is 0-based; day 0 of next month).
const daysInMonth = (year, month) =>
    new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

// The free-report window is a calendar-month window anchored to the user's
// signup day-of-month (createdAt) — must match the Cloud Function's freeWindow.
// Falls back to day-1 (calendar month) when createdAt is absent.
const freeWindow = (createdAt, nowMs = Date.now()) => {
    // Firestore Timestamp -> millis; tolerate a plain Date.
    const createdAtMs = createdAt?.toMillis
        ? createdAt.toMillis()
        : createdAt instanceof Date
            ? createdAt.getTime()
            : null
    const anchorDay = createdAtMs ? new Date(createdAtMs).getUTCDate() : 1
    const now = new Date(nowMs)
    const anniversary = (y, m) =>
        Date.UTC(y, m, Math.min(anchorDay, daysInMonth(y, m)))

    let start = anniversary(now.getUTCFullYear(), now.getUTCMonth())
    if (start > nowMs) {
        start = anniversary(now.getUTCFullYear(), now.getUTCMonth() - 1)
    }
    const s = new Date(start)
    const nextReset = anniversary(s.getUTCFullYear(), s.getUTCMonth() + 1)
    return { key: s.toISOString().slice(0, 10), nextResetMs: nextReset }
}

export const AuthContextProvider = ({children}) => {

    const [user, setUser]  = useState(null)
    const [loading, setLoading] = useState(true)
    const [needsRoleSelection, setNeedsRoleSelection] = useState(false)
    const [subscription, setSubscription] = useState(null) // live users/{uid} billing fields

    // Derived subscription state
    const subscriptionStatus = subscription?.subscriptionStatus || 'free'
    const plan = subscription?.plan || null
    const isProUser = (() => {
        if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') return true
        const end = subscription?.currentPeriodEnd
        // Firestore Timestamp -> millis; keep access until the paid period ends.
        if (end?.toMillis && end.toMillis() > Date.now()) return true
        return false
    })()
    const reportWindow = freeWindow(subscription?.createdAt)
    const reportCount = subscription?.reportPeriod === reportWindow.key
        ? (subscription?.reportCount || 0)
        : 0
    const remaining = isProUser ? Infinity : Math.max(0, FREE_LIMIT - reportCount)

    // Stripe Checkout / Customer Portal (proxied through Cloud Functions).
    // Both open in a new tab so the app stays put; see utils/popup.js for why
    // the tab is opened before the callable rather than after it.
    const startCheckout = async (planName, placeholderText) =>
        openInNewTab(async () => {
            const fn = httpsCallable(functions, 'createCheckoutSession')
            const { data } = await fn({ plan: planName })
            return data.url
        }, placeholderText)

    const openPortal = async (placeholderText) =>
        openInNewTab(async () => {
            const fn = httpsCallable(functions, 'createPortalSession')
            const { data } = await fn()
            return data.url
        }, placeholderText)

    // In-app monthly <-> yearly switch (no tab hop). Swaps the price on the
    // existing subscription with immediate proration; the users/{uid} snapshot
    // then reflects the new plan.
    const switchPlan = async (planName) => {
        const fn = httpsCallable(functions, 'switchPlan')
        const { data } = await fn({ plan: planName })
        return data
    }

    // Graceful cancel: the subscription ends at the current period end and the
    // user keeps Pro access until then. Reversible via resumeSubscription. The
    // users/{uid} snapshot flips cancelAtPeriodEnd/cancelAt once it resolves.
    const cancelSubscription = async () => {
        const fn = httpsCallable(functions, 'cancelSubscription')
        const { data } = await fn()
        return data
    }

    // Undo a pending cancel before it takes effect.
    const resumeSubscription = async () => {
        const fn = httpsCallable(functions, 'resumeSubscription')
        const { data } = await fn()
        return data
    }

    // Google Sign In
    const googleSignIn = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            console.log("sign in successful");
            let { uid, displayName, email, photoURL } = result.user;
            console.log({uid, displayName, email, photoURL});
            console.log(result.user);
            
            // Save Google user data to Firestore if not already exists
            const userDoc = await getDoc(doc(db, "users", result.user.uid));
            if (!userDoc.exists()) {
                await setDoc(doc(db, "users", result.user.uid), {
                    uid: result.user.uid,
                    name: displayName || "Google User",
                    email: email,
                    createdAt: new Date(),
                    emailVerified: result.user.emailVerified
                });
            }
            
            return result;
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    // Email/Password Sign Up
    const emailSignUp = async (email, password, name, role) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            // Update the user's display name
            await updateProfile(user, {
                displayName: name
            });

            // Send email verification
            await sendEmailVerification(user);

            // Save user data to Firestore
            await setDoc(doc(db, "users", user.uid), {
                uid: user.uid,
                name: name,
                email: email,
                role: role,
                createdAt: new Date(),
                emailVerified: false
            });

            return userCredential;
        } catch (error) {
            console.error("Error signing up:", error);
            throw error;
        }
    }

    // Email/Password Sign In
    const emailSignIn = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential;
        } catch (error) {
            console.error("Error signing in:", error);
            throw error;
        }
    }

    // Forgot Password
    const forgotPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return true;
        } catch (error) {
            console.error("Error sending password reset email:", error);
            throw error;
        }
    }

    // Resend Email Verification
    const resendEmailVerification = async () => {
        try {
            if (user && !user.emailVerified) {
                await sendEmailVerification(user);
                return true;
            }
            throw new Error("No user logged in or email already verified");
        } catch (error) {
            console.error("Error sending email verification:", error);
            throw error;
        }
    }

    // Update User Role
    const updateUserRole = async (role) => {
        try {
            if (user) {
                await setDoc(doc(db, "users", user.uid), {
                    role: role
                }, { merge: true });
                setNeedsRoleSelection(false);
                return true;
            }
            throw new Error("No user logged in");
        } catch (error) {
            console.error("Error updating user role:", error);
            throw error;
        }
    }

    // Skip Role Selection
    const skipRoleSelection = () => {
        setNeedsRoleSelection(false);
    }

    // Logout
    const logOut = () => {
        return signOut(auth);
    }

    const deleteAccount = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error("No user logged in");
        }
        const uid = currentUser.uid;
        // Cancel any live Stripe subscription before deleting anything — the
        // Firestore doc below holds stripeCustomerId/stripeSubscriptionId, and
        // once it's gone that billing info is unrecoverable. If this throws,
        // deleteAccount aborts and nothing is deleted, so the user can retry
        // instead of silently keeping a subscription with no account left.
        const cancelSubscription = httpsCallable(functions, 'cancelSubscriptionForAccountDeletion')
        await cancelSubscription()

        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
            await deleteDoc(userRef);
        }
        const reportsQuery = query(collection(db, "sharedReports"), where("userId", "==", uid));
        const reportsSnap = await getDocs(reportsQuery);
        await Promise.all(reportsSnap.docs.map(d => deleteDoc(d.ref)));
        await deleteUser(currentUser);
    };

    useEffect(() =>{
        let unsubscribeUserDoc = null;
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser)
            setLoading(false)
            console.log('User ', currentUser);

            // Tear down any previous user-doc listener
            if (unsubscribeUserDoc) {
                unsubscribeUserDoc();
                unsubscribeUserDoc = null;
            }

            // Check if user needs role selection
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setNeedsRoleSelection(!userData.role);
                    } else {
                        setNeedsRoleSelection(true);
                    }
                } catch (error) {
                    console.error("Error checking user role:", error);
                    setNeedsRoleSelection(true);
                }

                // Live subscription / report-count state from the user doc
                // (updated server-side by the Stripe webhook and the gate).
                unsubscribeUserDoc = onSnapshot(
                    doc(db, "users", currentUser.uid),
                    (snap) => setSubscription(snap.exists() ? snap.data() : null),
                    (error) => console.error("Error listening to user doc:", error)
                );
            } else {
                setNeedsRoleSelection(false);
                setSubscription(null);
            }
        })
        return () => {
            if (unsubscribeUserDoc) unsubscribeUserDoc();
            unsubscribe();
        }
    }, [])

    return(
        <AuthContext.Provider value={{
            googleSignIn, 
            emailSignUp, 
            emailSignIn, 
            forgotPassword, 
            resendEmailVerification, 
            updateUserRole,
            skipRoleSelection,
            logOut,
            deleteAccount,
            user,
            loading,
            needsRoleSelection,
            subscriptionStatus,
            plan,
            isProUser,
            reportCount,
            remaining,
            freeLimit: FREE_LIMIT,
            resetDate: new Date(reportWindow.nextResetMs),
            currentPeriodEnd: subscription?.currentPeriodEnd?.toMillis
                ? new Date(subscription.currentPeriodEnd.toMillis())
                : null,
            cancelAtPeriodEnd: !!subscription?.cancelAtPeriodEnd,
            cancelAt: subscription?.cancelAt?.toMillis
                ? new Date(subscription.cancelAt.toMillis())
                : null,
            hasStripeCustomer: !!subscription?.stripeCustomerId,
            startCheckout,
            switchPlan,
            cancelSubscription,
            resumeSubscription,
            openPortal
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export const UserAuth = () => {
    return useContext(AuthContext)
}